import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { EyeIcon } from '@/icons';
import { cccdAdmin, type AdminEvent, type AdminTeamListItem } from '@/lib/api/cccdAdminClient';
import { TeamStatusBadge } from '@/components/cccd/TeamStatusBadge';
import { EventCategoriesSection } from '@/components/cccd/EventCategoriesSection';
import { EventPlansSection } from '@/components/cccd/EventPlansSection';
import { EventCatalogItemsSection } from '@/components/cccd/EventCatalogItemsSection';
import { EventSponsorsSection } from '@/components/cccd/EventSponsorsSection';
import { PodiumResultsSection } from '@/components/cccd/PodiumResultsSection';
import { ScoringResultsGrid } from '@/components/cccd/ScoringResultsGrid';
import { ScoringLeaderboard } from '@/components/cccd/ScoringLeaderboard';
import { LaunchControlSection } from '@/components/cccd/LaunchControlSection';
import { ProjectionButtons } from '@/components/cccd/ProjectionButtons';
import { EventShell, type EventTabKey } from '@/components/cccd/EventShell';
import { StatusPill } from '@/components/cccd/StatusPill';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { EventActiveToggle } from '@/components/cccd/EventActiveToggle';
import { TeamsListCard } from '@/components/cccd/TeamsListCard';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import { type DataTableColumn, type DataTableSort } from '@/components/ui/table/DataTable';
import { TEAMS_PAGE_SIZE, sortTeams } from '@/lib/cccdTeamStatus';
import { inputCls, labelCls, textareaCls } from '@/components/admin/FormComponents';
import { RecordSaveStatusBar } from '@/components/admin/RecordSaveStatusBar';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import Alert from '@/components/ui/alert/Alert';
import { getErrorMessage, getValidationErrorMessage } from '@/lib/errors';
import { toastError, toastSuccess } from '@/lib/toast';
import { durationPill, registrationWindowPill, toDateTimeLocal, validateEventDates } from '@/lib/cccdDatePills';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { buildEventStatMetrics } from '@/lib/cccdEventMetrics';
import { toCsv, downloadCsv } from '@/lib/csv';
import { teamCsvTemplateHeaders } from '@/lib/teamsCsvImport';
import type { CccdTeamStatus } from '@/lib/api/cccdAdminClient';
import { LoadState } from "@/components/ui/LoadState"

type Tab = EventTabKey;

const TAB_KEYS: Tab[] = ['info', 'equipos', 'configuracion', 'podio', 'puntaje', 'clasificacion', 'lanzamiento'];
const TEAMS_LIMIT = 200;

function EventInfoForm({ event, onSaved }: { event: AdminEvent; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: event.name,
    eventStartDate: toDateTimeLocal(event.eventStartDate),
    eventEndDate: toDateTimeLocal(event.eventEndDate),
    registrationStartDate: toDateTimeLocal(event.registrationStartDate),
    registrationDeadline: toDateTimeLocal(event.registrationDeadline),
    consentText: event.consentText,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      name: event.name,
      eventStartDate: toDateTimeLocal(event.eventStartDate),
      eventEndDate: toDateTimeLocal(event.eventEndDate),
      registrationStartDate: toDateTimeLocal(event.registrationStartDate),
      registrationDeadline: toDateTimeLocal(event.registrationDeadline),
      consentText: event.consentText,
    });
  }, [event]);

  const saveMutation = useMutation({
    mutationFn: () =>
      cccdAdmin.updateEvent(event.id, {
        name: form.name,
        eventStartDate: new Date(form.eventStartDate).toISOString(),
        eventEndDate: new Date(form.eventEndDate).toISOString(),
        registrationStartDate: new Date(form.registrationStartDate).toISOString(),
        registrationDeadline: new Date(form.registrationDeadline).toISOString(),
        consentText: form.consentText,
      }),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const message = getValidationErrorMessage(err);
      setError(message);
      toastError(message);
    },
  });

  const f = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const eventDuration = durationPill(form.eventStartDate, form.eventEndDate);
  const registrationWindow = registrationWindowPill(form.registrationStartDate, form.registrationDeadline);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const invalid = validateEventDates(form);
        if (invalid) { setError(invalid); return; }
        setError('');
        saveMutation.mutate();
      }}
      className="space-y-4 pb-20"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionHeader
          title="Identificación"
          subtitle="Nombre visible del evento"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" /><path d="M4 10h16M9 4v6" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <label htmlFor="event-info-name" className={labelCls}>Nombre del evento *</label>
          <input id="event-info-name" required className={`min-h-11 ${inputCls}`} {...f('name')} />
        </SectionHeader>

        <SectionHeader
          title="Fechas del concurso"
          subtitle="Cuándo se corre el evento presencialmente"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={eventDuration && <StatusPill tone={eventDuration.tone} label={eventDuration.label} />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="event-info-start" className={labelCls}>Fecha de inicio *</label>
              <input id="event-info-start" required type="datetime-local" className={`min-h-11 ${inputCls}`} {...f('eventStartDate')} />
            </div>
            <div>
              <label htmlFor="event-info-end" className={labelCls}>Fecha de cierre *</label>
              <input id="event-info-end" required type="datetime-local" className={`min-h-11 ${inputCls}`} {...f('eventEndDate')} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Inscripciones"
          subtitle="Ventana en la que los equipos pueden registrarse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={registrationWindow && <StatusPill tone={registrationWindow.tone} label={registrationWindow.label} />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="event-info-reg-start" className={labelCls}>Apertura de inscripciones *</label>
              <input id="event-info-reg-start" required type="datetime-local" className={`min-h-11 ${inputCls}`} {...f('registrationStartDate')} />
            </div>
            <div>
              <label htmlFor="event-info-reg-end" className={labelCls}>Cierre de inscripciones *</label>
              <input id="event-info-reg-end" required type="datetime-local" className={`min-h-11 ${inputCls}`} {...f('registrationDeadline')} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Consentimiento"
          subtitle="Texto legal que el equipo debe aceptar al inscribirse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <label htmlFor="event-info-consent" className="sr-only">Texto de consentimiento</label>
          <textarea id="event-info-consent" required rows={3} className={textareaCls} {...f('consentText')} />
        </SectionHeader>
      </div>

      {error && <Alert variant="error" title="No se pudo guardar" message={error} />}
      <RecordSaveStatusBar
        status={saveMutation.isPending ? 'saving' : saveMutation.isSuccess ? 'saved' : 'idle'}
        onForceSync={() => saveMutation.mutate()}
      />
    </form>
  );
}

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // La pestaña vive en la URL (?tab=configuracion): se puede enlazar y se conserva al volver desde los formularios.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tab, setTabState] = useState<Tab>(TAB_KEYS.includes(tabParam as Tab) ? (tabParam as Tab) : 'info');
  const setTab = (next: Tab) => {
    setTabState(next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    }, { replace: true });
  };
  type PendingAction = 'close' | 'feature' | 'tickets-on' | null;
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [statusFilter, setStatusFilter] = useState<CccdTeamStatus | 'all'>('all');
  const [teamsSearch, setTeamsSearch] = useState('');
  const [teamsSort, setTeamsSort] = useState<DataTableSort>({ column: 'createdAt', dir: 'desc' });
  const [teamsPage, setTeamsPage] = useState(1);

  const { data: event } = useQuery({
    queryKey: ['event', id],
    queryFn: () => cccdAdmin.getEvent(id!),
    enabled: !!id,
  });

  const { data: teamsData, isLoading: teamsLoading, isFetching: teamsFetching, refetch: refetchTeams } = useQuery({
    queryKey: ['teams', { eventId: id }],
    queryFn: () => cccdAdmin.listTeams({ eventId: id!, limit: TEAMS_LIMIT }),
    enabled: !!id && (tab === 'equipos' || tab === 'podio' || tab === 'puntaje' || tab === 'clasificacion' || tab === 'lanzamiento'),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['event-categories', id],
    queryFn: () => cccdAdmin.getEventCategories(id!),
    enabled: !!id && (tab === 'equipos' || tab === 'podio' || tab === 'puntaje' || tab === 'clasificacion' || tab === 'lanzamiento'),
  });

  const allTeams: AdminTeamListItem[] = teamsData?.teams ?? [];
  const teamsTruncated = (teamsData?.total ?? 0) > allTeams.length;
  const categoryNameBySlug = (slug: string) => categories.find((c) => c.slug === slug)?.name ?? slug;
  const statusFilteredTeams = statusFilter === 'all' ? allTeams : allTeams.filter((t) => t.status === statusFilter);
  const searchedTeams = teamsSearch.trim()
    ? statusFilteredTeams.filter((t) => t.name.toLowerCase().includes(teamsSearch.trim().toLowerCase()))
    : statusFilteredTeams;
  const filteredTeams = searchedTeams;
  const sortedTeams = sortTeams(filteredTeams, teamsSort);
  const teams = sortedTeams.slice((teamsPage - 1) * TEAMS_PAGE_SIZE, teamsPage * TEAMS_PAGE_SIZE);

  function onStatusFilterChange(s: CccdTeamStatus | 'all') {
    setStatusFilter(s);
    setTeamsPage(1);
  }

  function onTeamsSearchChange(value: string) {
    setTeamsSearch(value);
    setTeamsPage(1);
  }

  async function onDownloadTemplate() {
    if (!id) return;
    const [cats, plans] = await Promise.all([cccdAdmin.getEventCategories(id), cccdAdmin.getEventPlans(id)]);
    const headers = teamCsvTemplateHeaders(true);
    const exampleRow = [
      'Equipo Ejemplo',
      cats[0]?.slug ?? 'categoria-slug',
      plans[0]?.slug ?? 'plan-slug',
      'Colegio Ejemplo',
      '',
      '',
      'no',
    ];
    downloadCsv(`equipos-plantilla-${event?.slug ?? id}.csv`, toCsv([headers, exampleRow]));
  }

  // El listado de eventos usa useServerTable: su caché cuelga de ['server-table', '/cccd/admin/events'].
  const refreshEventQueries = () => {
    qc.invalidateQueries({ queryKey: ['event', id] });
    qc.invalidateQueries({ queryKey: ['events'] });
    qc.invalidateQueries({ queryKey: ['server-table', '/cccd/admin/events'] });
  };

  // Toggle de detalle — aplica de inmediato, revierte si el servidor rechaza.
  // Ver design.md, receta "Toggle de detalle".
  const toggleActiveMutation = useMutation({
    mutationFn: (nextIsActive: boolean) => cccdAdmin.updateEvent(id!, { isActive: nextIsActive }),
    onMutate: async (nextIsActive) => {
      const previous = await applyOptimistic<AdminEvent>(qc, ['event', id], (prev) =>
        prev ? { ...prev, isActive: nextIsActive } : prev,
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      rollbackOptimistic(qc, ['event', id], context?.previous);
      toastError(`No se pudo actualizar el estado del evento. ${getErrorMessage(err)}`);
    },
    onSettled: () => refreshEventQueries(),
  });
  const toggleActiveChipStatus = toggleActiveMutation.isPending ? 'saving' : toggleActiveMutation.isError ? 'error' : 'idle';

  // cccd-team-showcase: destacar este evento hace que sus equipos/categorías
  // aparezcan en Competidores de Academy, y desmarca cualquier otro evento
  // nacional que lo estuviera (el backend aplica esa exclusividad).
  const toggleFeaturedMutation = useMutation({
    mutationFn: () => cccdAdmin.updateEvent(id!, { isFeaturedInAcademy: true }),
    onSuccess: () => toastSuccess('Evento destacado en Academy.'),
    onError: (err) => toastError(`No se pudo destacar el evento en Academy. ${getErrorMessage(err)}`),
    onSettled: () => refreshEventQueries(),
  });
  const toggleFeaturedChipStatus = toggleFeaturedMutation.isPending ? 'saving' : toggleFeaturedMutation.isError ? 'error' : 'idle';

  // cccd-event-attendance-tickets: activa que los integrantes de equipo vean/
  // descarguen su boleto QR — el backend emite en bloque los boletos
  // faltantes al activar.
  const toggleAttendeeTicketsMutation = useMutation({
    mutationFn: (next: boolean) => cccdAdmin.toggleEventAttendeeTickets(id!, next),
    onSuccess: (result, next) => {
      if (!next) toastSuccess('Boletos de asistencia desactivados.');
      else {
        const issued = result.ticketsIssued ?? 0;
        toastSuccess(issued > 0 ? `Boletos activados: se emitieron ${issued} ${issued === 1 ? 'boleto' : 'boletos'}.` : 'Boletos de asistencia activados.');
      }
    },
    onError: (err) => toastError(`No se pudo actualizar el estado de los boletos de asistencia. ${getErrorMessage(err)}`),
    onSettled: () => refreshEventQueries(),
  });
  const toggleAttendeeTicketsChipStatus = toggleAttendeeTicketsMutation.isPending
    ? 'saving'
    : toggleAttendeeTicketsMutation.isError
      ? 'error'
      : 'idle';

  const columns: DataTableColumn<AdminTeamListItem>[] = [
    { key: 'name', label: 'Equipo', sortable: true, render: (t) => <span className="font-medium text-gray-900 dark:text-white">{t.name}</span> },
    {
      key: 'captain',
      label: 'Capitán',
      render: (t) => t.captain ? (
        <>
          <div>{t.captain.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{t.captain.email}</div>
        </>
      ) : (
        <span className="text-xs text-gray-500 dark:text-gray-400">Sin responsable</span>
      ),
    },
    { key: 'categoryId', label: 'Categoría', render: (t) => <span className="text-gray-600 dark:text-gray-400">{t.categoryId ? categoryNameBySlug(t.categoryId) : '—'}</span> },
    { key: 'status', label: 'Estado', sortable: true, render: (t) => <TeamStatusBadge status={t.status} /> },
    {
      key: 'createdAt',
      label: 'Registro',
      sortable: true,
      render: (t) => <span className="text-xs text-gray-500">{new Date(t.createdAt).toLocaleDateString('es-CO')}</span>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (t) => (
        <div className="flex justify-end">
          <RowActionsMenu actions={[{ label: 'Ver equipo', icon: <EyeIcon />, to: `/cccd/teams/${t.id}` }]} />
        </div>
      ),
    },
  ];

  return (
    <EventShell
      mode="edit"
      activeTab={tab}
      onTabChange={setTab}
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd/events' }, event?.name ?? '...']}
      title={event?.name ?? 'Cargando...'}
      subtitle="Concurso nacional CCCD"
      metrics={event ? buildEventStatMetrics(event, 'Equipos registrados en este evento') : []}
      headerActions={
        event ? (
          <>
            <ProjectionButtons id={event.id} />
            <EventActiveToggle
              isActive={event.isActive}
              onToggle={(next) => (next ? toggleActiveMutation.mutate(true) : setPendingAction('close'))}
              status={toggleActiveChipStatus}
              isFeaturedInAcademy={event.isFeaturedInAcademy}
              onToggleFeatured={() => setPendingAction('feature')}
              featuredStatus={toggleFeaturedChipStatus}
              attendeeTicketsEnabled={event.attendeeTicketsEnabled}
              onToggleAttendeeTickets={(next) => (next ? setPendingAction('tickets-on') : toggleAttendeeTicketsMutation.mutate(false))}
              attendeeTicketsStatus={toggleAttendeeTicketsChipStatus}
            />
          </>
        ) : undefined
      }
    >
      {!event ? (
        <LoadState />
      ) : (
        <>
          {/* Tab content */}
          {tab === 'info' && (
            <EventInfoForm event={event} onSaved={refreshEventQueries} />
          )}

          {tab === 'equipos' && teamsTruncated && (
            <div className="mb-4">
              <Alert
                variant="warning"
                title={`Se muestran ${allTeams.length} de ${teamsData?.total} equipos`}
                message="El listado tiene un tope de equipos por carga. Usa el buscador o el filtro de estado para acotar la lista."
              />
            </div>
          )}

          {tab === 'equipos' && (
            <TeamsListCard
              columns={columns}
              rows={teams}
              rowKey={(t) => t.id}
              loading={teamsLoading}
              statusFilter={statusFilter}
              onStatusFilterChange={onStatusFilterChange}
              onCreateTeam={() => navigate(`/cccd/teams/new?eventId=${id}`)}
              onDownloadTemplate={onDownloadTemplate}
              onImportTeams={() => navigate(`/cccd/teams/import?eventId=${id}`)}
              search={teamsSearch}
              onSearchChange={onTeamsSearchChange}
              onRefresh={() => refetchTeams()}
              refreshing={teamsFetching}
              sort={teamsSort}
              onSortChange={setTeamsSort}
              page={teamsPage}
              pageSize={TEAMS_PAGE_SIZE}
              total={sortedTeams.length}
              onPageChange={setTeamsPage}
            />
          )}

          {tab === 'configuracion' && (
            <DndProvider backend={HTML5Backend}>
              <div className="space-y-10">
                <EventCategoriesSection parent={{ type: 'event', eventId: id! }} />
                <EventPlansSection parent={{ type: 'event', eventId: id! }} />
                <EventCatalogItemsSection eventId={id!} />
                <EventSponsorsSection parent={{ type: 'event', id: id! }} />
              </div>
            </DndProvider>
          )}

          {tab === 'puntaje' && (
            <ScoringResultsGrid categories={categories} />
          )}

          {tab === 'clasificacion' && (
            <ScoringLeaderboard categories={categories} />
          )}

          {tab === 'lanzamiento' && (
            <DndProvider backend={HTML5Backend}>
              <LaunchControlSection categories={categories} />
            </DndProvider>
          )}

          {tab === 'podio' && (
            <PodiumResultsSection
              target={{ type: 'event', eventId: id!, categories: categories.map((c) => ({ slug: c.slug, name: c.name })) }}
              teams={allTeams.map((t) => ({ id: t.id, name: t.name }))}
            />
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={pendingAction === 'close'}
        danger={false}
        title="¿Cerrar las inscripciones?"
        message="Los equipos dejarán de poder inscribirse en este evento. Puedes volver a abrirlas cuando quieras."
        confirmLabel="Cerrar inscripciones"
        onConfirm={() => { setPendingAction(null); toggleActiveMutation.mutate(false); }}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDialog
        isOpen={pendingAction === 'feature'}
        danger={false}
        title="¿Destacar este evento en Academy?"
        message="Sus equipos y categorías aparecerán en Competidores y Documentos de Academy. Si otro evento nacional estaba destacado, dejará de estarlo."
        confirmLabel="Destacar en Academy"
        onConfirm={() => { setPendingAction(null); toggleFeaturedMutation.mutate(); }}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDialog
        isOpen={pendingAction === 'tickets-on'}
        danger={false}
        title="¿Activar los boletos de asistencia?"
        message="Se emitirá el boleto QR de todos los integrantes que aún no lo tengan, y podrán verlo y descargarlo."
        confirmLabel="Activar boletos"
        onConfirm={() => { setPendingAction(null); toggleAttendeeTicketsMutation.mutate(true); }}
        onCancel={() => setPendingAction(null)}
      />
    </EventShell>
  );
}
